

























































































































































































































































































































import {
  defineComponent,
  ref,
  watch,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount
} from '@nuxtjs/composition-api';
import useWindowResize from '~/hooks/useResizeWindow';
import { useHeader, useMegaMenu, useKECart, usePDP, useCurrentUser } from '@vue-storefront/novulo';
import { onSSR, sharedRef, useVSFContext } from '@vue-storefront/core';
import { SfLink } from '@storefront-ui/vue';
import { mediaUrlFallback } from '@/helpers/utils';
import useUrlFormatter from '~/hooks/useUrlFormatter';
import useDefaultHeaderToggle from '~/hooks/useDefaultHeaderToggle';
import useCurrentPage from '~/hooks/useCurrentPage';
import useMinicartDrawer from '~/composables/useMinicartDrawer';

export default defineComponent({
  name: 'KEHeader',
  components: {
    SfLink
  },
  setup() {
    const { shouldHideHeader } = useDefaultHeaderToggle();
    const isMobileMenuOpen = ref(false);
    const isMainMenuOpen = sharedRef(false, 'main-menu-open');
    const tabActive = ref('');
    const isOpen = ref(false);
    const isAlgoliaFlyoutOpen = ref(false);
    const headerRef = ref<HTMLElement | null>(null);
    const navHeader = ref<HTMLElement | null>(null);
    const spotlightBarRef = ref<HTMLElement | null>(null);
    const showOverlay = ref(false);
    const { getHeader, headerResult } = useHeader();
    const { getMegaMenu, megaMenuResult } = useMegaMenu();
    const {
      isOpen: isCartOpen,
      setIsCartOpen,
      totalPrice,
      filteredCartItems,
      lastAddedItem,
      cartItemsCount,
      loading
    } = useKECart();
    const {
      isEnabled: cartDrawerEnabled,
      drawerOpen,
      drawerProduct,
      isDrawerLoading,
      drawerCartItemId,
      drawerCartItemIsInsured,
      openDrawer,
      closeDrawer
    } = useMinicartDrawer();
    const { getPDP, pdpResult } = usePDP('header');
    const formatUrl = useUrlFormatter();
    const { $novulo } = useVSFContext();
    const salesChannel = $novulo.config.state.getSalesChannel();
    const { isHomePage } = useCurrentPage();
    const { currentUser } = useCurrentUser();

    const srcLogo = computed(() => {
      if (!headerResult.value)
        return {
          media_path: '',
          media_file_name: ''
        };
      const logoMedia = headerResult.value.find(
        (d) => d.code === 'header_country_logo'
      )?.elements?.[0]?.media?.[0];
      return {
        media_path: mediaUrlFallback(logoMedia?.media_path),
        media_file_name: logoMedia?.media_file_name
      };
    });

    const hasBottomLogo = computed(() => {
      return (
        headerResult?.value?.find((d) => d.code === 'header_country_logo')
          ?.elements?.[0]?.ke_logo === false
      );
    });

    // get all different tabs from the megamenu items' parentCategory
    const tabs = computed(() => {
      if (!Array.isArray(megaMenuResult.value)) return [];
      // tabs come from the api unordered, so we need to order them according to this array
      const parentIdOrder = ['Producten', 'Merken', 'Inspiratie', 'Service'];
      const mappedMegamenu = megaMenuResult.value.map((item) => ({
        parentId: item.parentId,
        mainDesc: item.mainDesc
      }));
      const availableTabs = new Set(
        mappedMegamenu
          .filter(
            (item) => parentIdOrder.includes(item.parentId) && item.mainDesc
          )
          .sort(
            (a, b) =>
              parentIdOrder.indexOf(a.parentId) -
              parentIdOrder.indexOf(b.parentId)
          )
          .map((item) => item.parentId)
      );

      // Keep inspiration tab only on specific sales channels
      const channelsWithInspiration = [10, 12, 57, 79, 69].includes(
        salesChannel
      );
      if (!channelsWithInspiration) availableTabs.delete('Inspiratie');

      return Array.from(availableTabs);
    });

    const extraLinkTabs = computed(() => {
      return (
        headerResult.value.find((d) => d.code === 'header_mega_menu_buttons')
          ?.elements?.[0]?.urls || []
      );
    });
    const getCategories = computed(() => {
      const categories = {};
      if (!Array.isArray(megaMenuResult.value)) return {};
      megaMenuResult.value.forEach((element) => {
        if (!categories.hasOwnProperty(element.parentId)) {
          categories[element.parentId] = [element];
        } else {
          categories[element.parentId].push(element);
        }
      });
      return categories;
    });

    const spotlightBarData = computed(() => {
      return headerResult.value?.[0]?.spotlight_bar?.[0]?.content || null;
    });

    const homeLink = formatUrl('/');

    const uspsGetter = computed(() => {
      if (!Array.isArray(headerResult.value)) return [];
      return headerResult.value.find((d) => d.code === 'header_usps')
        ?.elements?.[0]?.urls;
    });

    const quickLinksGetter = (position: string) => {
      if (!Array.isArray(headerResult.value)) return [];

      /* from backend
       * top: between 10 and 19 (inclusive)
       * navigation: between 20 and 29 (inclusive)
       */
      const positionFilters = {
        navigation: (item) => item.position >= 20 && item.position < 30,
        top: (item) => item.position >= 10 && item.position < 20
      };

      const positionFilter = positionFilters[position] || positionFilters.top;
      const links =
        headerResult.value
          .find((d) => d.code === 'header_basic_links')
          ?.elements?.[0]?.urls?.filter(positionFilter) || [];
      return links.sort((a, b) => a.position - b.position);
    };

    onSSR(async () => {
      await getMegaMenu();
      await getHeader();
    });

    watch(
      () => isMobileMenuOpen.value,
      (value) => {
        isMainMenuOpen.value = value;
        const body = document.querySelector('body');
        if (value) {
          if (body) {
            nextTick(() => {
              document.body.classList.add('scroll-lock');
            });
          }
        } else {
          body.classList.remove('scroll-lock');
        }
      }
    );

    // dropdowns
    const isLoginDropdownOpen = ref(false);
    const handleToggleLoginDropdown = (newValue = null) => {
      if (newValue === null) {
        isLoginDropdownOpen.value = !isLoginDropdownOpen.value;
      } else {
        isLoginDropdownOpen.value = newValue;
      }
      if (isLoginDropdownOpen.value) {
        setIsCartOpen(false);
      }
    };

    const isCartDropdownOpen = ref(false);
    const handleToggleCartDropdown = (newValue = null) => {
      isCartDropdownOpen.value = Boolean(newValue);

      if (isCartDropdownOpen.value) {
        isLoginDropdownOpen.value = false;
      }
    };
    const updateHeaderHeight = () => {
      if (!navHeader.value) return;

      const headerHeight = navHeader.value.offsetHeight;
      const spotlightHeight = spotlightBarRef.value
        ? spotlightBarRef.value.offsetHeight
        : 0;

      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty('--header-height-total', `${headerHeight + spotlightHeight}px`);
    };

    const menuLinksContainer = computed(
      () => headerResult.value?.find(container => (container.code === 'header_home_quick_links')) ?? null
    );

    const hasMenuLinks = computed(() => {
      if (menuLinksContainer.value)
        return Boolean(menuLinksContainer.value?.elements[0]?.urls);
      return false;
    });

    const showQuicklinksMenu = computed(() => {
      return hasMenuLinks.value && isHomePage.value;
    });

    let showOverlayTimeout;

    const closeMobileMenu = (event?: Event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      clearTimeout(showOverlayTimeout);
      showOverlay.value = false;
      isMobileMenuOpen.value = false;
    };

    watch(tabActive, (newVal) => {
      clearTimeout(showOverlayTimeout);

      if (Boolean(newVal) && tabs.value.includes(newVal)) {
        showOverlayTimeout = setTimeout(() => {
          showOverlay.value = true;
        }, 300);
      } else {
        showOverlay.value = false;
      }
    });

    const openLegacyDropdown = () => {
      isCartDropdownOpen.value = true;
      setIsCartOpen(false);
    };

    watch(isCartOpen, async (open) => {
      if (!open) return;

      if (!cartDrawerEnabled.value) {
        openLegacyDropdown();
        return;
      }

      const last = lastAddedItem.value ?? (filteredCartItems.value || []).slice(-1)[0];

      const isToggleInsurance = last?.isToggleInsurance ?? null;
      if (isToggleInsurance) {
        openDrawer({ isLoading: false, cartItemIsInsured: last?.cartItemIsInsured });
        setIsCartOpen(false);
        return;
      }

      if (drawerOpen.value) {
        return;
      }

      const spliId = last?.spliId || null;
      const productId = last?.product_id || last?.productId || null;
      if (!spliId || !productId) {
        openLegacyDropdown();
        return;
      }

      openDrawer({ isLoading: true });

      try {
        await getPDP(spliId, productId);

        if (!pdpResult.value) {
          throw new Error('Empty PDP response');
        }

        openDrawer({
          isLoading: false,
          product: pdpResult.value,
          cartItemId: last?.cartItemId ?? null,
          cartItemIsInsured: last?.cartItemIsInsured ?? null
        });
      } catch (e) {
        closeDrawer();
        openLegacyDropdown();
      } finally {
        setIsCartOpen(false);
      }
    });

    const { isDesktop } = useWindowResize();
    const AUTO_OPEN_KEY = 'loginDropdownAutoOpened';
    const autoOpenLoginTimer = ref<number | null>(null);
    const scheduleAutoOpenIfEligible = () => {
      if (!isHomePage.value) return;
      if (!isDesktop.value) return;
      if (process.client && sessionStorage.getItem(AUTO_OPEN_KEY) === '1') return;

      if (autoOpenLoginTimer.value !== null) {
        clearTimeout(autoOpenLoginTimer.value);
        autoOpenLoginTimer.value = null;
      }

      autoOpenLoginTimer.value = window.setTimeout(() => {
        if (!isLoginDropdownOpen.value && !currentUser.value?.id && !isCartDropdownOpen.value && !isOpen.value && !isAlgoliaFlyoutOpen.value) {
          handleToggleLoginDropdown(true);
          try {
            sessionStorage.setItem(AUTO_OPEN_KEY, '1');
          } catch { /* silent */
          }
        }
        autoOpenLoginTimer.value = null;
      }, 3000);
    };

    onMounted(() => {
      updateHeaderHeight();
      window.addEventListener('resize', updateHeaderHeight);
      scheduleAutoOpenIfEligible();
    });

    watch(() => isLoginDropdownOpen.value, (open) => {
      if (open && autoOpenLoginTimer.value !== null) {
        clearTimeout(autoOpenLoginTimer.value);
        autoOpenLoginTimer.value = null;
      }
    });

    onBeforeUnmount(() => {
      isMainMenuOpen.value = false;
      if (autoOpenLoginTimer.value !== null) {
        clearTimeout(autoOpenLoginTimer.value);
        autoOpenLoginTimer.value = null;
      }
    });

    const totalsLoading = computed(() => loading.value?.updateCart === true);
    const handleAlgoliaFlyoutToggle = (isOpenState: boolean) => {
      isAlgoliaFlyoutOpen.value = isOpenState;
    };

    return {
      QUICK_LINKS_TYPE: {
        navigation: 'navigation',
        top: 'top'
      },
      cartDrawerEnabled,
      cartItemsCount,
      closeDrawer,
      closeMobileMenu,
      drawerOpen,
      drawerProduct,
      drawerCartItemId,
      drawerCartItemIsInsured,
      extraLinkTabs,
      formatUrl,
      totalsLoading,
      getCategories,
      handleAlgoliaFlyoutToggle,
      handleToggleCartDropdown,
      handleToggleLoginDropdown,
      hasBottomLogo,
      headerResult,
      homeLink,
      isCartDropdownOpen,
      isDrawerLoading,
      isAlgoliaFlyoutOpen,
      isLoginDropdownOpen,
      isMobileMenuOpen,
      isOpen,
      isDesktop,
      megaMenuResult,
      menuLinksContainer,
      quickLinksGetter,
      shouldHideHeader,
      showOverlay,
      showQuicklinksMenu,
      spotlightBarData,
      srcLogo,
      tabActive,
      tabs,
      navHeader,
      headerRef,
      spotlightBarRef,
      totalPrice,
      uspsGetter
    };
  }
});
